/**
 * Test Queue — the tester's calm, always-there list of online reports they
 * still owe (tech-side only). Tapping a card opens the same ReportInputCard the
 * gate uses, so filing from the queue and filing from the nudge are identical.
 *
 * #950 (SPEC C1–C6) adds the redesigned presentation behind `field_redesign_v2`:
 * the tab says what it is and whose job it is, the empty state teaches instead
 * of shaming, and every row says which city, which job, WHY it's the tech's,
 * and how long it has been waiting — oldest first. Flag off renders exactly
 * today's tab.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageSpinner } from "@/components/ui/spinner";
import { ReportInputCard } from "@/components/report-input-card";
import { getTesterQueue, getReportInputPacket, hoursSince, type TesterQueueItem } from "@/lib/report-input";
import { useFieldRedesign } from "@/lib/use-field-redesign";
import {
    OLDEST_FIRST_NOTE,
    REPORTS_EMPTY_PARAGRAPHS,
    ageChipLabel,
    cityFilingIdentity,
    reportsTabCopy,
    sentReceipt,
    whyThisIsYours,
} from "./redesign-screens-copy";
import { Clock, ExternalLink, CheckCircle2 } from "lucide-react";

/**
 * Oldest first (SPEC C3). A tester works the list top-down, so the one that
 * has been waiting longest has to BE at the top — and the row says why it's
 * there, because an order nobody can explain reads as random.
 *
 * Rows with no test time sort last rather than first: an unknown age is not
 * evidence of urgency, and putting it on top would push a genuinely old one
 * down.
 */
export function oldestFirst(items: TesterQueueItem[]): TesterQueueItem[] {
    const at = (i: TesterQueueItem): number => {
        if (!i.testAt) return Number.POSITIVE_INFINITY;
        const t = new Date(i.testAt).getTime();
        return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    return [...items].sort((a, b) => at(a) - at(b));
}

export function TestQueueTab() {
    const qc = useQueryClient();
    const [openJob, setOpenJob] = useState<string | null>(null);
    /** C6 — the receipt for the one just filed, so "sent" is a moment, not a
     *  row silently vanishing. Session-scoped on purpose: nothing writes the
     *  `filed` state yet, so there is no confirmed history to render. */
    const [justSent, setJustSent] = useState<{ city: string | null; site: string | null } | null>(null);
    const { enabled: redesign } = useFieldRedesign();

    const queue = useQuery({ queryKey: ["report-input", "queue"], queryFn: getTesterQueue });
    const packet = useQuery({
        queryKey: ["report-input", "packet", openJob],
        queryFn: () => getReportInputPacket(openJob as string),
        enabled: !!openJob,
    });

    if (queue.isLoading) return <PageSpinner />;
    const rawItems = queue.data?.items ?? [];
    const items = redesign ? oldestFirst(rawItems) : rawItems;
    const copy = reportsTabCopy();

    const modal = openJob && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
            {/* 17 photos run the card past an iPad screen; cap it and let it scroll so
                the file-it steps and Mark submitted stay reachable. */}
            <div className="max-h-[calc(100dvh-1.5rem)] w-full max-w-md overflow-y-auto overscroll-contain rounded-2xl bg-background p-5 shadow-2xl">
                {packet.isLoading || !packet.data ? (
                    <PageSpinner />
                ) : (
                    <ReportInputCard
                        packet={packet.data.packet}
                        variant="queue"
                        redesign={redesign}
                        onResolved={() => {
                            if (redesign) {
                                const p = packet.data?.packet;
                                setJustSent({ city: p?.city ?? null, site: p?.siteName ?? null });
                            }
                            void qc.invalidateQueries({ queryKey: ["report-input"] });
                            setOpenJob(null);
                        }}
                        onDismiss={() => setOpenJob(null)}
                    />
                )}
            </div>
        </div>
    );

    // ── Redesigned tab (C1–C6) ──────────────────────────────────────────────
    if (redesign) {
        const receipt = justSent ? sentReceipt(justSent.city, justSent.site) : null;
        return (
            <div className="flex flex-col gap-3">
                {receipt && (
                    <div
                        data-testid="reports-sent-receipt"
                        className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2.5 dark:border-emerald-500/40 dark:bg-emerald-950/30"
                    >
                        <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-800 dark:text-emerald-200">
                            <CheckCircle2 className="h-4 w-4 shrink-0" />
                            {receipt.headline}
                        </p>
                        <p className="mt-0.5 text-xs text-emerald-900/90 dark:text-emerald-100/90">{receipt.body}</p>
                    </div>
                )}

                <p className="text-xs text-foreground/70" data-testid="reports-subtitle">
                    {copy.subtitle}. Only you can — it&rsquo;s tied to your tester license.
                </p>

                {items.length === 0 ? (
                    <Card>
                        <CardContent className="py-10 text-center" data-testid="reports-empty">
                            <CheckCircle2 className="mx-auto mb-3 h-9 w-9 text-emerald-500" />
                            <h3 className="mb-2 text-base font-semibold">{copy.emptyTitle}</h3>
                            <div className="mx-auto max-w-sm space-y-2 text-left text-xs text-foreground/80">
                                {REPORTS_EMPTY_PARAGRAPHS.map((p) => (
                                    <p key={p}>{p}</p>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    items.map((it, index) => {
                        const age = ageChipLabel(hoursSince(it.testAt));
                        return (
                            <Card
                                key={it.jobId}
                                className="cursor-pointer"
                                data-testid="reports-queue-row"
                                onClick={() => setOpenJob(it.jobId)}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-sm font-bold leading-tight">
                                                {it.city ? `City of ${it.city}` : "This city"}
                                            </div>
                                            <div className="text-xs text-foreground/70">
                                                {cityFilingIdentity({ isPortal: it.isPortal, platform: it.platform })}
                                            </div>
                                        </div>
                                        {age && (
                                            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] tabular-nums text-foreground/70">
                                                <Clock className="h-3 w-3" />
                                                {age}
                                            </span>
                                        )}
                                    </div>

                                    <div className="mt-2 text-sm font-semibold">{it.siteName ?? "Job"}</div>
                                    <div className="text-xs text-foreground/70">
                                        {[it.deviceLabel, it.serialNumber && `SN ${it.serialNumber}`]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </div>

                                    {/* C3 — the row says whose job this is and why,
                                        so the list reads as a reason and not a nag. */}
                                    <p className="mt-2 text-xs text-foreground/80" data-testid="reports-why-yours">
                                        {whyThisIsYours(it.city)}
                                        {index === 0 && items.length > 1 ? ` ${OLDEST_FIRST_NOTE}` : ""}
                                    </p>

                                    {it.reason && (
                                        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs dark:border-amber-900 dark:bg-amber-950/50">
                                            “{it.reason}” <span className="text-foreground/70">— office</span>
                                        </div>
                                    )}

                                    {it.accountNumber && (
                                        <div className="mt-2 text-[11px] text-foreground/70">
                                            City account # {it.accountNumber}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })
                )}

                {modal}
            </div>
        );
    }

    // ── Today's tab, unchanged (the rollback promise) ───────────────────────
    if (items.length === 0) {
        return (
            <div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <p className="text-sm font-semibold">You&rsquo;re all caught up.</p>
                <p className="text-xs">No online reports waiting on you.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <p className="text-xs text-muted-foreground">
                Reports still to put in the city portal. Knock these out — most take a couple minutes.
            </p>
            {items.map((it: TesterQueueItem) => {
                const hrs = hoursSince(it.testAt);
                return (
                    <Card key={it.jobId} className="cursor-pointer" onClick={() => setOpenJob(it.jobId)}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Badge
                                    variant="outline"
                                    className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
                                >
                                    Waiting on you
                                </Badge>
                                {hrs != null && (
                                    <span className="inline-flex items-center gap-1 text-[11px] tabular-nums text-muted-foreground">
                                        <Clock className="h-3 w-3" />
                                        tested {hrs}h ago
                                    </span>
                                )}
                            </div>
                            <div className="mt-2 text-sm font-bold">{it.siteName ?? "Job"}</div>
                            <div className="text-xs text-muted-foreground">
                                {[it.city, it.deviceLabel, it.serialNumber && `SN ${it.serialNumber}`]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </div>
                            {it.reason && (
                                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs dark:border-amber-900 dark:bg-amber-950/50">
                                    “{it.reason}” <span className="text-muted-foreground">— office</span>
                                </div>
                            )}
                            <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                                <span className="inline-flex items-center gap-1 font-semibold text-primary">
                                    <ExternalLink className="h-3 w-3" />
                                    {it.platform ?? "portal"}
                                </span>
                                {it.accountNumber && <span>Acct #{it.accountNumber}</span>}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}

            {modal}
        </div>
    );
}
